import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Users, 
  CalendarCheck, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Search,
  Filter,
  ChevronRight,
  Shield,
  Building2,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Get admin dashboard stats
  const { data: dashboardData, isLoading: dashLoading } = useQuery({
    queryKey: ["adminDashboard"],
    queryFn: async () => {
      const res = await axios.get(`${API}/admin/dashboard`);
      return res.data;
    }
  });

  // Get promoter list
  const { data: promotersData, isLoading: promotersLoading, refetch } = useQuery({
    queryKey: ["adminPromoters", statusFilter, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      const res = await axios.get(`${API}/admin/promoters?${params}`);
      return res.data;
    }
  });

  // Verify promoter mutation
  const verifyMutation = useMutation({
    mutationFn: async ({ promoterId, approved, reason }) => {
      const res = await axios.post(`${API}/admin/promoters/${promoterId}/verify`, {
        approved,
        rejection_reason: reason
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.status === "verified" ? "Promoter approved!" : "Application rejected");
      queryClient.invalidateQueries(["adminPromoters"]);
      queryClient.invalidateQueries(["adminDashboard"]);
      setSelectedPromoter(null);
      setRejectionReason("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  });

  const stats = dashboardData || { promoter_stats: {}, event_stats: {}, user_stats: {} };
  const promoters = promotersData?.promoters || [];

  if (dashLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">Manage promoters and events</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-purple-500/20 text-purple-400 px-3 py-1.5 rounded-full text-xs">
            <Shield className="w-3.5 h-3.5" />
            Admin Access
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Total Users" 
            value={stats.user_stats?.total || 0}
            color="text-blue-400"
          />
          <StatCard 
            icon={Clock} 
            label="Pending Promoters" 
            value={stats.promoter_stats?.pending || 0}
            color="text-amber-400"
            highlight={stats.promoter_stats?.pending > 0}
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Verified Promoters" 
            value={stats.promoter_stats?.verified || 0}
            color="text-emerald-400"
          />
          <StatCard 
            icon={CalendarCheck} 
            label="Live Events" 
            value={stats.event_stats?.live || 0}
            color="text-pink-400"
          />
        </div>

        {/* Promoter Management */}
        <div className="glass rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-semibold">Promoter Applications</h2>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 bg-white/5 border-white/10 w-full sm:w-[200px]"
                />
              </div>
              
              {/* Status Filter */}
              <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                {["pending", "verified", "rejected", ""].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      statusFilter === status 
                        ? "bg-pink-500 text-white" 
                        : "text-muted-foreground hover:text-white"
                    }`}
                  >
                    {status || "All"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Promoter List */}
          {promotersLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : promoters.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No promoters found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoters.map((promoter) => (
                <PromoterRow 
                  key={promoter.promoter_id} 
                  promoter={promoter}
                  onClick={() => setSelectedPromoter(promoter)}
                />
              ))}
            </div>
          )}

          {/* Pagination info */}
          {promotersData?.total > 0 && (
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Showing {promoters.length} of {promotersData.total} promoters
            </p>
          )}
        </div>
      </div>

      {/* Promoter Detail Modal */}
      {selectedPromoter && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="glass w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold">{selectedPromoter.business_name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{selectedPromoter.business_type}</p>
                </div>
                <StatusBadge status={selectedPromoter.status} />
              </div>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedPromoter.user_email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedPromoter.contact_phone}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedPromoter.business_address}, {selectedPromoter.parish}</span>
                </div>
                {selectedPromoter.website && (
                  <div className="flex items-center gap-3 text-sm">
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    <a href={selectedPromoter.website} target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">
                      {selectedPromoter.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Experience */}
              <div className="mb-6">
                <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Experience</h4>
                <p className="text-sm bg-white/5 rounded-lg p-3">{selectedPromoter.experience_description}</p>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Expected Events/Year</p>
                  <p className="font-semibold">{selectedPromoter.expected_events_per_year}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Submitted</p>
                  <p className="font-semibold">{new Date(selectedPromoter.submitted_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Actions */}
              {selectedPromoter.status === "pending" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Rejection Reason (optional)</label>
                    <Input
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Reason for rejection..."
                      className="mt-1 bg-white/5 border-white/10"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => verifyMutation.mutate({ 
                        promoterId: selectedPromoter.promoter_id, 
                        approved: true 
                      })}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={verifyMutation.isPending}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => verifyMutation.mutate({ 
                        promoterId: selectedPromoter.promoter_id, 
                        approved: false,
                        reason: rejectionReason
                      })}
                      variant="outline"
                      className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                      disabled={verifyMutation.isPending}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {selectedPromoter.status === "rejected" && selectedPromoter.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-400 font-medium">Rejection Reason</p>
                  <p className="text-sm mt-1">{selectedPromoter.rejection_reason}</p>
                </div>
              )}

              <Button
                onClick={() => { setSelectedPromoter(null); setRejectionReason(""); }}
                variant="ghost"
                className="w-full mt-4"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, highlight }) => (
  <div className={`glass rounded-xl p-4 ${highlight ? "ring-2 ring-amber-500/50" : ""}`}>
    <Icon className={`w-5 h-5 ${color} mb-2`} />
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: "bg-amber-500/20 text-amber-400", text: "Pending" },
    verified: { color: "bg-emerald-500/20 text-emerald-400", text: "Verified" },
    rejected: { color: "bg-red-500/20 text-red-400", text: "Rejected" }
  };
  
  const { color, text } = config[status] || config.pending;
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      {text}
    </span>
  );
};

const PromoterRow = ({ promoter, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all text-left"
    data-testid={`promoter-${promoter.promoter_id}`}
  >
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
      <Building2 className="w-5 h-5 text-pink-400" />
    </div>
    
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{promoter.business_name}</p>
      <p className="text-xs text-muted-foreground truncate">
        {promoter.user_email} • {promoter.parish}
      </p>
    </div>
    
    <div className="flex items-center gap-3">
      <StatusBadge status={promoter.status} />
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  </button>
);

export default AdminDashboard;
